import Mustache from 'mustache';
import moment from 'moment';
import GeckoStore from 'services/gecko-store';
import GeckoFeatures from 'services/gecko-features';

const getMoment = (date) => {
  const timestampRegex = /[0-9]{10}/;
  if (timestampRegex.test(date)) {
    return moment(date, 'X');
  }
  return moment(new Date(date));
};

const formatDate = (date, format) => getMoment(date).format(format);

const capitalize = (text) => {
  return text.replace(/\w\S*/g, (word) => {
    return `${word.charAt(0).toUpperCase()}${word.substr(1).toLowerCase()}`;
  });
};

const parseContext = (context) => {
  return context.reduce((parsedContext, value) => {
    if (value.type === 'group') {
      return Object.assign({}, parsedContext, {
        [value.tag]: parseContext(value.value),
      });
    }
    return Object.assign({}, parsedContext, { [value.tag]: value.value });
  }, {});
};

const defaultContact = () => {
  const fields = GeckoStore.redux
    .get('fields')
    .filter((field) => field.custom_field === true);
  return {
    first_name: 'Example',
    last_name: 'Contact',
    full_name: 'Example Contact',
    email: 'example.contact@example.com',
    tel: '+447712345678',
    last_message_time: new Date(),
    custom_fields: fields.reduce((custom_fields, field) => {
      if (field.type === 'number') {
        return Object.assign({}, custom_fields, { [field.id]: 123 });
      }
      if (field.type === 'date') {
        return Object.assign({}, custom_fields, {
          [field.id]: new Date().toString(),
        });
      }
      if (field.type === 'email') {
        return Object.assign({}, custom_fields, {
          [field.id]: 'example.contact@example.com',
        });
      }
      if (field.type === 'telephone') {
        return Object.assign({}, custom_fields, {
          [field.id]: '+447712345678',
        });
      }
      if (field.type === 'textarea') {
        return Object.assign({}, custom_fields, {
          [field.id]: 'example\nmultiline\ntext',
        });
      }
      if (
        field.type === 'select' &&
        field.field_options &&
        field.options &&
        field.options.length > 0
      ) {
        return Object.assign({}, custom_fields, {
          [field.id]: field.field_options[0].id,
        });
      }
      if (
        field.type === 'select_multi' &&
        field.field_options &&
        field.options &&
        field.options.length > 2
      ) {
        return Object.assign({}, custom_fields, {
          [field.id]: [field.field_options[0].id, field.field_options[1].id],
        });
      }
      if (
        field.type === 'select_multi' &&
        field.field_options &&
        field.options &&
        field.options.length > 0
      ) {
        return Object.assign({}, custom_fields, {
          [field.id]: [field.field_options[0].id],
        });
      }
      return Object.assign({}, custom_fields, { [field.id]: 'example value' });
    }, {}),
  };
};

export const buildContactGroup = (contact = defaultContact()) => {
  const fields = GeckoStore.redux.get('fields');
  return {
    label: 'Contact',
    tag: 'contact',
    type: 'group',
    value: [
      {
        label: 'Full Name',
        tag: 'full_name',
        type: 'text',
        value:
          contact.first_name || contact.last_name
            ? contact.full_name
            : undefined,
      },
      {
        label: 'First Name',
        tag: 'first_name',
        type: 'text',
        value: contact.first_name,
      },
      {
        label: 'Last Name',
        tag: 'last_name',
        type: 'text',
        value: contact.last_name,
      },
      {
        label: 'Email',
        tag: 'email',
        type: 'text',
        value: contact.email,
      },
      {
        label: 'Telephone',
        tag: 'telephone',
        type: 'text',
        value: contact.tel,
      },
      {
        label: 'Last Message Time',
        tag: 'last_message_time',
        type: 'date_time',
        value: contact.last_message_time,
      },
      {
        label: 'Custom Fields',
        tag: 'custom_fields',
        type: 'group',
        value: Object.keys(contact.custom_fields).reduce((tags, key) => {
          const field = fields.find(
            (field) => Number(field.id) === Number(key)
          );
          if (!field) {
            return tags;
          }
          if (field.type === 'display') {
            return tags;
          }
          if (field.type === 'date') {
            return tags.concat({
              label: field.label,
              tag: key,
              type: 'date_time',
              value: contact.custom_fields[key],
            });
          }
          if (field.type === 'select_multi') {
            const fieldValues = contact.custom_fields[key].map((value) => {
              return field.field_options.find((option) => option.id === value)
                .label;
            });
            return tags.concat({
              label: field.label,
              tag: key,
              type: 'list',
              value: fieldValues,
            });
          }
          if (field.type === 'select') {
            const fieldValue = field.field_options.find(
              (option) => option.id === contact.custom_fields[key]
            );
            return tags.concat({
              label: field.label,
              tag: key,
              type: 'text',
              value: fieldValue?.label || '',
            });
          }
          if (field.type === 'textarea') {
            return tags.concat({
              label: field.label,
              tag: key,
              type: 'multiline_text',
              value: contact.custom_fields[key],
            });
          }
          return tags.concat({
            label: field.label,
            tag: key,
            type: 'text',
            value: contact.custom_fields[key],
          });
        }, []),
      },
    ],
  };
};

export const buildAgentGroup = (agent) => {
  return {
    label: 'Agent',
    tag: 'agent',
    type: 'group',
    value: [
      {
        label: 'Full Name',
        tag: 'full_name',
        type: 'text',
        value: agent.full_name,
      },
      {
        label: 'Email',
        tag: 'email',
        type: 'text',
        value: agent.email,
      },
      {
        label: 'Telephone',
        tag: 'telephone',
        type: 'text',
        value: agent.tel,
      },
    ],
  };
};

const friendlyTypes = {
  live_chat: 'Live Chat',
  mailgun: 'Email',
  twilio: 'SMS',
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter',
  wechat: 'WeChat',
};

const additionalChannelTags = (channel) => {
  if (channel.type === 'twilio') {
    return [
      {
        label: 'Telephone',
        tag: 'telephone',
        type: 'text',
        value: channel.configuration.phone_number,
      },
    ];
  }
  if (channel.type === 'facebook') {
    return [
      {
        label: 'Facebook Link',
        tag: 'page_link',
        type: 'link',
        value: `https://www.facebook.com/${channel.configuration.page_id}`,
      },
    ];
  }
  if (channel.type === 'mailgun') {
    return [
      {
        label: 'Reply to Email',
        tag: 'reply_to',
        type: 'text',
        value: channel.configuration.reply_to,
      },
      {
        label: 'Sender Name',
        tag: 'sender_name',
        type: 'text',
        value: channel.configuration.sender_name,
      },
      {
        label: 'Email',
        tag: 'email',
        type: 'text',
        value: channel.configuration.pretty_email,
      },
    ];
  }
  if (channel.type === 'twitter') {
    return [
      {
        label: 'Twitter Link',
        tag: 'page_link',
        type: 'link',
        value: `https://twitter.com/intent/user?user_id=${channel.configuration.twitter_id}`,
      },
    ];
  }
  return [];
};

export const buildSelectedChannelGroup = ({
  channel = null,
  showChannelQueuePosition,
}) => {
  const hasChannelSelected = channel !== null;

  if (!hasChannelSelected && !showChannelQueuePosition) {
    return {};
  }

  const getChannelQueuePositionValue = () => {
    return {
      label: 'Channel Queue Position',
      tag: 'channel_queue_position',
      type: 'text',
      value: 'Position Number',
    };
  };

  if (!hasChannelSelected && showChannelQueuePosition) {
    return {
      label: 'Selected Channel',
      tag: 'selected_channel',
      type: 'group',
      value: [getChannelQueuePositionValue()],
    };
  }

  const defaultValues = [
    {
      label: 'Name',
      tag: 'name',
      type: 'text',
      value: channel.name,
    },
    {
      label: 'Type',
      tag: 'type',
      type: 'text',
      value: friendlyTypes[channel.type],
    },
  ];

  if (hasChannelSelected && !showChannelQueuePosition) {
    return {
      label: 'Selected Channel',
      tag: 'selected_channel',
      type: 'group',
      value: [...defaultValues, ...additionalChannelTags(channel)],
    };
  }

  if (hasChannelSelected && showChannelQueuePosition) {
    let group = {
      label: 'Selected Channel',
      tag: 'selected_channel',
      type: 'group',
      value: [
        ...defaultValues,
        ...additionalChannelTags(channel),
        getChannelQueuePositionValue(),
      ],
    };

    return group;
  }

  throw new Error('Unexpected combination to build a SelectedChannelGroup');
};

export const buildLastMessageChannelGroup = (channel) => {
  return {
    label: 'Last Message Channel',
    tag: 'last_message_channel',
    type: 'group',
    value: [
      {
        label: 'Name',
        tag: 'name',
        type: 'text',
        value: channel.name,
      },
      {
        label: 'Type',
        tag: 'type',
        type: 'text',
        value: friendlyTypes[channel.type],
      },
      ...additionalChannelTags(channel),
    ],
  };
};

// Overwrite the toString method on the passed in function so that it can't be rendered in tags
const safeFunc = (func) => {
  func.toString = () => '';
  return func;
};

export const formatFunctions = {
  date_time: {
    Long: () =>
      safeFunc((text, render) => formatDate(render(text), 'Do MMMM YYYY')),
    Short: () =>
      safeFunc((text, render) => formatDate(render(text), 'DD/MM/YYYY')),
    'Long with time': () =>
      safeFunc((text, render) =>
        formatDate(render(text), 'Do MMMM YYYY [at] HH:mm')
      ),
    'Short with time': () =>
      safeFunc((text, render) =>
        formatDate(render(text), 'DD/MM/YYYY [at] HH:mm')
      ),
    Time: () => safeFunc((text, render) => formatDate(render(text), 'HH:mm')),
    'From now': () =>
      safeFunc((text, render) => getMoment(render(text)).fromNow()),
  },
  text: {
    Capitalize: () => safeFunc((text, render) => capitalize(render(text))),
    'Upper case': () => safeFunc((text, render) => render(text).toUpperCase()),
    'Lower case': () => safeFunc((text, render) => render(text).toLowerCase()),
  },
  multiline_text: {
    Capitalize: () => safeFunc((text, render) => capitalize(render(text))),
    'Upper case': () => safeFunc((text, render) => render(text).toUpperCase()),
    'Lower case': () => safeFunc((text, render) => render(text).toLowerCase()),
  },
  list: {
    'Bullet points': () =>
      safeFunc((text, render) => {
        const array = render(text)
          .split(',')
          .map((item) => item.trim());
        return array.map((line) => `- ${line}`).join('\n');
      }),
    'Numbered list': () =>
      safeFunc((text, render) => {
        const array = render(text)
          .split(',')
          .map((item) => item.trim());
        return array.map((line, index) => `${index + 1}. ${line}`).join('\n');
      }),
    'New lines': () =>
      safeFunc((text, render) => {
        const array = render(text)
          .split(',')
          .map((item) => item.trim());
        return array.join('\n');
      }),
  },
  link: {
    Link: () =>
      safeFunc((text, render) => {
        return render(`{${text}}`);
      }),
  },
};

/* 
  processTags
  Mustashe {{ }} standard double tags auto escapes special character including " ' " which would render as " &#39;"
  https://github.com/mustache/mustache#escaping
  This function wraps an extra set of bracket around each tag {{{ }}} which disables escaping
  HOWEVER, it will not do it on any tag which itself uses a special character e.g. {{#format.text.Upper case}}
*/
const processTags = (template) => {
  //split template by tags e.g. {{anything}}
  const tags = template.split(/({{[^}}]*}})/);
  const result = tags.map((tag) => {
    //if item start with special mustashe tag e.g. {{#
    const startsWithSpecialCharRegex = /^{{[#&><!\/]/;
    if (startsWithSpecialCharRegex.test(tag)) {
      return tag; //dont add extra brackets
    }
    // otherwise if item is regular tag, wrap it in extra brackets to NOT escape special chars
    return tag.replace('{{', '{{{').replace('}}', '}}}');
  });
  // flatten
  return result.join('');
};

export default (template, _context) => {
  const processedTemplateString = processTags(template);

  const context = {
    ...{ format: formatFunctions },
    ...parseContext(_context),
  };

  return Mustache.render(processedTemplateString, context);
};
